import React from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { useData } from '../context/DataContext';
import { Award } from 'lucide-react';

const Awards = () => {
  const { data } = useData();
  const awardsList = Array.isArray(data.awards) 
    ? data.awards 
    : [...(data.awards?.national || []), ...(data.awards?.state || []), ...(data.awards?.safety || [])];

  return (
    <div className="min-h-screen bg-ivory pb-24">
      <PageHeader 
        title="Honors & Recognition" 
        subtitle="A testament to our unwavering commitment to quality, safety, and sustainable excellence in the sugarcane industry." 
      />

      {/* Awards Grid Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {awardsList.length === 0 ? (
            <div className="py-16 text-center premium-card max-w-sm mx-auto">
              <Award className="text-slate/20 mx-auto mb-4" size={40} />
              <h4 className="text-md font-heading font-bold text-charcoal mb-1">No Awards Published</h4>
              <p className="text-xs text-slate/50 px-6">Check back later for updates on our honors and achievements.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {awardsList.map((award, aIdx) => (
                <motion.div
                  key={award.id || aIdx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: aIdx * 0.05 }}
                  className="group premium-card overflow-hidden flex flex-col justify-between"
                >
                  <div>
                    <div className="relative aspect-[16/10] overflow-hidden bg-mist">
                      <img 
                        src={award.image || 'https://images.unsplash.com/photo-1578574515313-ad99999849c6?q=80&w=800&auto=format&fit=crop'} 
                        alt={award.title}
                        className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-105"
                      />
                      {award.year && (
                        <div className="absolute top-4 left-4">
                          <span className="px-3.5 py-1 bg-white/95 backdrop-blur-md rounded-lg text-gold text-[10px] font-heading font-extrabold shadow-sm uppercase tracking-wider border border-gold/10">
                            {award.year}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-heading font-bold text-charcoal mb-3 leading-snug group-hover:text-emerald transition-colors min-h-[3rem] line-clamp-2">
                        {award.title}
                      </h3>
                      <p className="text-slate text-xs leading-relaxed line-clamp-3">
                        {award.desc || 'Recognized for excellence in technical efficiency and sustainable manufacturing practices.'}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 pt-4 mt-2 border-t border-gray-100 flex items-center justify-between bg-ivory/50">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-slate/40 uppercase font-heading font-bold tracking-wider mb-0.5">Presented By</span>
                      <span className="text-xs font-heading font-bold text-charcoal line-clamp-1">{award.presenter}</span>
                    </div>
                    <div className="w-8 h-8 bg-white border border-gray-100 rounded-lg flex items-center justify-center shadow-sm group-hover:bg-gold group-hover:border-gold transition-all">
                      <Award className="text-slate/30 group-hover:text-white transition-colors" size={16} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Quote */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-gold/20 text-6xl font-serif leading-none mb-4">"</div>
          <p className="text-xl md:text-2xl font-heading font-bold text-charcoal leading-relaxed mb-6 italic -mt-6">
            Awards are not our final goal, but they motivate us to push the boundaries of what is possible in sustainable manufacturing.
          </p>
          <div className="gold-divider mx-auto"></div>
        </div>
      </section>
    </div>
  );
};

export default Awards;
